/**
 * The Living Safe Corridor, painted (ADR-065, amendment C).
 *
 * The guidance lane is ROAD PAINT: a stationary strip laid along the whole
 * route at level build, crisp-edged like a bike lane — a soft fill between
 * two solid edge lines. It does not follow the robot; the robot drives onto
 * it or off it, which is exactly what "we'd prefer you in this area" means.
 * (The earlier robot-anchored ribbon read as an effect glued to the player,
 * and its feathered edges read as blur — play-testing rejected both.)
 *
 * Dynamic remains only what IS dynamic: the rounded waiting-bay marking that
 * appears where the corridor asks the robot to wait, and the debug conflict
 * marker. All give-way mechanics and telemetry live in the sim, untouched.
 *
 * Everything sits ~7 cm above the road: decals closer than that z-fight with
 * overlapping junction pieces at grazing angles and tear into shards.
 */

import { Color3 } from "@babylonjs/core/Maths/math.color";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import type { Scene } from "@babylonjs/core/scene";
import type { GuideEdges } from "../sim/guide_geometry";
import type { Simulation } from "../sim/simulation";

/** Points around the waiting-bay outline. */
const K = 40;

const BLUE = new Color3(0.25, 0.55, 1.0);

function flatMat(name: string, scene: Scene, color: Color3, alpha: number): StandardMaterial {
  const mat = new StandardMaterial(name, scene);
  mat.emissiveColor = color;
  mat.diffuseColor = Color3.Black();
  mat.specularColor = Color3.Black();
  mat.disableLighting = true;
  mat.alpha = alpha;
  mat.backFaceCulling = false;
  return mat;
}

/** A static two-column ribbon from world-space left/right edge points. */
function staticRibbon(
  name: string,
  scene: Scene,
  left: ReadonlyArray<readonly [number, number, number]>,
  right: ReadonlyArray<readonly [number, number, number]>,
  mat: StandardMaterial,
): Mesh {
  const n = left.length;
  const positions = new Float32Array(n * 2 * 3);
  const normals = new Float32Array(n * 2 * 3);
  const indices: number[] = [];
  for (let i = 0; i < n; i++) {
    positions.set(left[i]!, i * 6);
    positions.set(right[i]!, i * 6 + 3);
    normals[i * 6 + 1] = 1;
    normals[i * 6 + 4] = 1;
    if (i < n - 1) {
      const a = i * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }
  const mesh = new Mesh(name, scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.indices = indices;
  vd.normals = normals;
  vd.applyToMesh(mesh, false);
  mesh.material = mat;
  mesh.isPickable = false;
  return mesh;
}

/**
 * A closed stroke — the waiting bay's outline. Two columns across the
 * stroke, K sections around the loop, positions updated per frame.
 */
function loopStroke(name: string, scene: Scene, color: Color3): Mesh {
  const mesh = new Mesh(name, scene);
  const verts = K * 2;
  const positions = new Float32Array(verts * 3);
  const normals = new Float32Array(verts * 3);
  for (let i = 0; i < verts; i++) normals[i * 3 + 1] = 1;
  const indices: number[] = [];
  for (let i = 0; i < K; i++) {
    const a = i * 2;
    const b = ((i + 1) % K) * 2;
    indices.push(a, a + 1, b, a + 1, b + 1, b);
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.indices = indices;
  vd.normals = normals;
  vd.applyToMesh(mesh, true);
  // Updated every frame, but the bounding info was computed from zeros —
  // without this the frustum culler hides it away from the origin.
  mesh.alwaysSelectAsActiveMesh = true;
  mesh.material = flatMat(name + "Mat", scene, color, 0);
  mesh.isPickable = false;
  mesh.setEnabled(false);
  return mesh;
}

export class CorridorLayer {
  private readonly bay: Mesh;
  private readonly conflictMark: Mesh | null;
  private guideMeshes: Mesh[] = [];
  private guideRef: GuideEdges | null = null;
  private bayAlpha = 0;
  private bayHoldSince = Infinity;

  constructor(
    private readonly scene: Scene,
    private readonly debug: boolean,
  ) {
    this.bay = loopStroke("corridorBay", scene, BLUE);
    if (debug) {
      const mark = CreateTorus("conflictMark", { diameter: 1.2, thickness: 0.08, tessellation: 24 }, scene);
      const mm = new StandardMaterial("conflictMarkMat", scene);
      mm.emissiveColor = new Color3(1, 0.2, 0.2);
      mm.disableLighting = true;
      mark.material = mm;
      mark.isPickable = false;
      mark.setEnabled(false);
      this.conflictMark = mark;
    } else {
      this.conflictMark = null;
    }
    (this.bay.material as StandardMaterial).forceCompilation(this.bay);
  }

  /** Lay the painted lane for this level: fill plus two crisp edge lines. */
  private buildGuide(g: GuideEdges): void {
    for (const m of this.guideMeshes) m.dispose(false, true);
    this.guideMeshes = [];
    const n = g.cx.length;
    if (n < 2) return;
    const build = (
      name: string,
      lo: (h: number) => number,
      hi: (h: number) => number,
      y: number,
      mat: StandardMaterial,
    ): void => {
      const L: Array<[number, number, number]> = [];
      const R: Array<[number, number, number]> = [];
      for (let i = 0; i < n; i++) {
        // Edge-relative offsets: h is the half-width at this sample, and the
        // fill/edge-line variants are expressed as fractions of the band.
        const lx = g.L[i]![0];
        const lz = g.L[i]![1];
        const rx = g.R[i]![0];
        const rz = g.R[i]![1];
        const wx = rx - lx;
        const wz = rz - lz;
        const w = Math.hypot(wx, wz) || 1e-6;
        const h = w / 2;
        const ux = wx / w;
        const uz = wz / w;
        const mx = (lx + rx) / 2;
        const mz = (lz + rz) / 2;
        L.push([mx + ux * lo(h), y, mz + uz * lo(h)]);
        R.push([mx + ux * hi(h), y, mz + uz * hi(h)]);
      }
      this.guideMeshes.push(staticRibbon(name, this.scene, L, R, mat));
    };

    // Soft fill between two solid edge lines: reads as painted lane, not glow.
    build("guideFill", (h) => -h, (h) => h, 0.065, flatMat("guideFillMat", this.scene, BLUE, 0.16));
    const edgeMat = flatMat("guideEdgeMat", this.scene, BLUE, 0.55);
    build("guideEdgeL", (h) => -h, (h) => -h + 0.07, 0.075, edgeMat);
    build("guideEdgeR", (h) => h - 0.07, (h) => h, 0.075, edgeMat);
  }

  update(sim: Simulation): void {
    const path = sim.level.world.path;
    const ribbon = sim.corridorRibbon;
    if (ribbon !== this.guideRef) {
      this.guideRef = ribbon;
      if (ribbon) {
        this.buildGuide(ribbon);
      } else {
        for (const m of this.guideMeshes) m.dispose(false, true);
        this.guideMeshes = [];
      }
    }

    const plan = sim.corridor;
    if (!plan || !path || plan.stations.length === 0 || sim.ended) {
      this.bay.setEnabled(false);
      this.conflictMark?.setEnabled(false);
      return;
    }
    const t = sim.t;
    const r = sim.shape.r;

    // --- Waiting bay: a rounded road marking where the corridor says wait. --
    // Only for a GENUINE hold, debounced and faded: transient conflicts used
    // to conjure pockets for a tenth of a second, and the box strobed —
    // thirteen of sixteen appearances per run were under a quarter second.
    // Amendment F: the cap never stops anyone, so the bay must not read as a
    // barrier either — it appears only once the robot has actually settled
    // into waiting (near-stationary at a hold), confirming the choice rather
    // than announcing an obstacle. A player pushing through never sees it.
    const holding =
      plan.pocket !== null &&
      plan.pocket.s1 > plan.stations[0]!.s + 0.5 &&
      sim.robot.speed < 0.6 &&
      (plan.state === "WAITING_POCKET" || plan.state === "BLOCKED_RIGHT" || plan.speedCap < 1.5);
    if (holding && !Number.isFinite(this.bayHoldSince)) this.bayHoldSince = t;
    if (!holding) this.bayHoldSince = Infinity;
    const wanted = holding && t - this.bayHoldSince > 0.25 ? 0.5 : 0;
    const dtF = Math.min(0.1, this.scene.getEngine().getDeltaTime() / 1000);
    this.bayAlpha += (wanted - this.bayAlpha) * (1 - Math.exp((wanted > 0 ? -8 : -5) * dtF));
    if (this.bayAlpha > 0.02 && plan.pocket) {
      const pk = plan.pocket;
      const bs0 = Math.max(pk.s0, plan.stations[0]!.s - 0.5);
      const lo = pk.lo - r;
      const hi = pk.hi + r;
      const midD = (lo + hi) / 2;
      const halfD = Math.max(0.4, (hi - lo) / 2);
      const midS = (bs0 + pk.s1) / 2;
      const halfS = Math.max(0.7, (pk.s1 - bs0) / 2);
      const corner = Math.min(0.7, halfD, halfS);
      const bpos = this.bay.getVerticesData("position")!;
      const hS = Math.max(0.05, halfS - corner);
      const hD = Math.max(0.05, halfD - corner);
      for (let i = 0; i < K; i++) {
        // Rounded-rectangle perimeter: project a ray onto the inner rect and
        // push out along the residual — the standard clamp construction.
        const a = (i / K) * Math.PI * 2;
        const px = Math.cos(a) * (halfS + corner) * 1.2;
        const py = Math.sin(a) * (halfD + corner) * 1.2;
        const qx = Math.min(hS, Math.max(-hS, px));
        const qy = Math.min(hD, Math.max(-hD, py));
        const dl = Math.hypot(px - qx, py - qy) || 1;
        const nS = (px - qx) / dl;
        const nD = (py - qy) / dl;
        const j = i * 6;
        const put = (c: number, off: number): void => {
          const along = qx + nS * (corner + off);
          const d = midD + qy + nD * (corner + off);
          const q = path.point(midS + along);
          bpos[j + c * 3] = q.x + Math.cos(q.theta) * d;
          bpos[j + c * 3 + 1] = 0.08;
          bpos[j + c * 3 + 2] = q.z - Math.sin(q.theta) * d;
        };
        put(0, 0.05);
        put(1, -0.05);
      }
      this.bay.updateVerticesData("position", bpos);
      this.bay.setEnabled(true);
      (this.bay.material as StandardMaterial).alpha = this.bayAlpha;
    } else {
      this.bay.setEnabled(false);
    }

    // --- Debug: the conflict point, marked. ---------------------------------
    if (this.conflictMark) {
      if (plan.conflictS !== null) {
        const p = path.point(plan.stations[0]!.s + plan.conflictS);
        this.conflictMark.position.set(p.x, 0.1, p.z);
        this.conflictMark.setEnabled(true);
      } else {
        this.conflictMark.setEnabled(false);
      }
    }
  }
}
